import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Badge, Loader, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import { visionSummaryLabel } from '@/lib/visionSummary';
import { AuditStatusBadge, ScoreBadge } from './UiAuditsPage';

/**
 * One audit run (`/app-ui-audit/:id`) — findings grouped by page then viewport, with the
 * screenshot for each view beside them.
 *
 * Screenshots are private objects (`private:<key>` in app_agent_files), so they load
 * through /api/agent/files/:id, which checks company ownership and streams. There is
 * deliberately no public URL to paste into a ticket; the HTML report carries the same
 * authenticated links.
 */

const SEVERITY_ORDER = ['blocker', 'high', 'medium', 'enhancement'];
const SEVERITY_VARIANT = {
  blocker: 'error', high: 'warning', medium: 'warning', enhancement: 'primary',
};

function SeverityBadge({ severity }) {
  return <Badge variant={SEVERITY_VARIANT[severity] || 'secondary'} size="sm">{severity}</Badge>;
}

/** 'measured' findings are reproducible arithmetic over the DOM; 'reviewed' ones are a
 *  model's judgement. Collapsing the two would hide which is which from whoever has to
 *  decide what to fix first. */
function SourceTag({ source }) {
  const reviewed = source === 'vision';
  return (
    <span
      className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[var(--app-border)]/40 text-[var(--app-muted)]"
      title={reviewed ? "A model's judgement over the screenshot" : 'Measured from the live DOM and computed styles'}
    >
      {reviewed ? 'reviewed' : 'measured'}
    </span>
  );
}

function FindingRow({ finding }) {
  return (
    <li className="border-t border-[var(--app-border)] py-3">
      <div className="flex gap-2 items-baseline flex-wrap">
        <SeverityBadge severity={finding.severity} />
        <span className="font-medium">{finding.title}</span>
        <SourceTag source={finding.source} />
      </div>
      {finding.heuristic && <div className="text-xs text-[var(--app-muted)] mt-0.5">{finding.heuristic}</div>}
      <p className="text-sm mt-1">{finding.description}</p>
      {finding.selector && (
        <code className="inline-block mt-1 text-xs bg-[var(--app-border)]/30 px-1.5 py-0.5 rounded break-all">
          {finding.selector}
        </code>
      )}
      {finding.recommendation && (
        <div className="mt-2 text-sm rounded-md px-3 py-2 bg-[var(--app-border)]/20">
          <span className="text-xs font-semibold uppercase tracking-wide mr-1">Fix</span>
          {finding.recommendation}
        </div>
      )}
    </li>
  );
}

function ViewSection({ view }) {
  return (
    <section className="mt-5">
      <h4 className="text-xs uppercase tracking-wide text-[var(--app-muted)] mb-2">{view.viewport}</h4>
      {view.screenshotFileId ? (
        <figure className="m-0 mb-3">
          <img
            src={`/api/agent/files/${view.screenshotFileId}`}
            alt={`Screenshot at ${view.viewport} width`}
            loading="lazy"
            className="w-full rounded-md border border-[var(--app-border)]"
          />
          <figcaption className="text-xs text-[var(--app-muted)] mt-1">Visual reference — {view.viewport}</figcaption>
        </figure>
      ) : (
        <p className="text-xs text-[var(--app-muted)]">No screenshot was captured for this view.</p>
      )}
      <ul className="list-none p-0 m-0">
        {view.findings.map((f) => <FindingRow key={f.id} finding={f} />)}
      </ul>
    </section>
  );
}

/** Group flat finding rows into page → viewport, ordering both by their worst severity so
 *  a reader who stops after the first card has seen the worst of it. */
function groupFindings(findings) {
  const pages = new Map();
  for (const f of findings) {
    if (!pages.has(f.page_url)) {
      pages.set(f.page_url, { url: f.page_url, name: f.page_name || f.page_url, views: new Map() });
    }
    const page = pages.get(f.page_url);
    const key = f.viewport || 'desktop';
    if (!page.views.has(key)) page.views.set(key, { viewport: key, screenshotFileId: null, findings: [] });
    const view = page.views.get(key);
    view.findings.push(f);
    if (f.screenshot_file_id && !view.screenshotFileId) view.screenshotFileId = f.screenshot_file_id;
  }
  const rank = (items) => Math.min(...items.map((f) => SEVERITY_ORDER.indexOf(f.severity)));
  return [...pages.values()]
    .map((page) => ({
      ...page,
      views: [...page.views.values()].map((v) => ({
        ...v,
        findings: v.findings.slice().sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)),
      })),
    }))
    .sort((a, b) => rank(a.views.flatMap((v) => v.findings)) - rank(b.views.flatMap((v) => v.findings)));
}

function Stat({ label, value }) {
  return (
    <div>
      <span className="text-[var(--app-muted)]">{label}</span>
      <div className="font-medium">{value}</div>
    </div>
  );
}

/**
 * Whatever the run needs to say about itself.
 *
 * A finished run can still carry a note: the crawl no longer discards a whole audit when
 * one view's findings fail to save, so a `done` run with an `error` is a PARTIAL report
 * and must say so — quietly showing fewer findings than were found would be worse than
 * the failure it replaced.
 */
function RunNotice({ audit }) {
  if (audit.status === 'failed') {
    return <Alert variant="error" title="This run did not finish">{audit.error}</Alert>;
  }
  if (audit.status === 'done' && audit.error) {
    return <Alert variant="warning" title="This report may be incomplete">{audit.error}</Alert>;
  }
  return null;
}

function SummaryStats({ audit, findingCount, visionConnectionName }) {
  // Reads the OUTCOME columns, never the request — see lib/visionSummary.js.
  const label = visionSummaryLabel(audit);
  const detail = visionConnectionName ? `via “${visionConnectionName}”` : null;
  const cost = audit.cost_usd != null ? `$${Number(audit.cost_usd).toFixed(4)}` : '—';
  return (
    <div className="flex gap-6 flex-wrap text-sm">
      <Stat label="Views" value={audit.pages_audited ?? 0} />
      <Stat label="Findings" value={findingCount} />
      <Stat
        label="Visual review"
        value={(
          <>
            {label}
            {detail && (
              <span className="block text-xs font-normal text-[var(--app-muted)]">{detail}</span>
            )}
          </>
        )}
      />
      <Stat label="Cost" value={cost} />
    </div>
  );
}

function CountPills({ findings }) {
  const counts = SEVERITY_ORDER.map((sev) => ({ sev, n: findings.filter((f) => f.severity === sev).length }));
  return (
    <div className="flex gap-2 flex-wrap mt-2">
      {counts.map(({ sev, n }) => <Badge key={sev} variant={SEVERITY_VARIANT[sev]} size="sm">{n} {sev}</Badge>)}
    </div>
  );
}

export default function UiAuditDetail() {
  const { id } = useParams();
  const { fetchApi } = useAppApi();
  const [audit, setAudit] = useState(null);
  const [findings, setFindings] = useState([]);
  const [severity, setSeverity] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [visionConnections, setVisionConnections] = useState([]);

  const load = () => {
    fetchApi('uiAuditShow', id)
      .then((res) => { setAudit(res?.audit || null); setFindings(res?.findings || []); })
      .catch((ex) => setErr(ex?.response?.data?.error || ex.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Names for resolved_vision_connection_id. Its own request rather than a join on the
    // audit endpoint: the id is all the run needs to record, and this list is 3 rows.
    fetchApi('uiAuditVisionConnections')
      .then((res) => setVisionConnections(res?.connections || []))
      .catch(() => { /* the tile falls back to the model name alone */ });
  }, [id]);

  // Keep polling only while the run is still moving; a finished audit never changes.
  useEffect(() => {
    if (!audit || (audit.status !== 'pending' && audit.status !== 'running')) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [audit?.status]);

  const visionConnectionName = useMemo(() => {
    const match = visionConnections.find((c) => c.id === audit?.resolved_vision_connection_id);
    return match ? match.name : null;
  }, [visionConnections, audit?.resolved_vision_connection_id]);

  const visible = useMemo(
    () => (severity ? findings.filter((f) => f.severity === severity) : findings),
    [findings, severity],
  );
  const pages = useMemo(() => groupFindings(visible), [visible]);

  if (loading) return <PageShell><Loader /></PageShell>;
  if (err) return <PageShell><Alert variant="error" title="Could not load">{err}</Alert></PageShell>;
  if (!audit) return <PageShell><Alert variant="warning" title="Not found">That audit does not exist.</Alert></PageShell>;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title={audit.name || audit.base_url}
        subtitle={<span className="break-all">{audit.base_url}</span>}
        actions={(
          <div className="flex gap-2 items-center">
            <AuditStatusBadge status={audit.status} />
            <ScoreBadge score={audit.overall_score} />
          </div>
        )}
      />

      <RunNotice audit={audit} />
      {(audit.status === 'pending' || audit.status === 'running') && (
        <Alert variant="info" title={audit.status === 'pending' ? 'Queued' : 'Running'}>
          The browser is working through the target. Findings appear here as each view completes.
        </Alert>
      )}

      <Card className="p-4">
        <SummaryStats audit={audit} findingCount={findings.length} visionConnectionName={visionConnectionName} />
        <CountPills findings={findings} />
        <div className="flex gap-2 mt-3 flex-wrap items-center">
          <div className="w-44">
            <Select
              options={[{ label: 'All severities', value: '' }, ...SEVERITY_ORDER.map((s) => ({ label: s, value: s }))]}
              value={severity}
              onChange={setSeverity}
              placeholder="All severities"
            />
          </div>
          {/* A real <a>, not <Button as={Link}>: the library's Button hardcodes a <button>
              and ignores `as`, so `as={Link}` rendered a button with a dead `to` and this link
              did nothing. It is a file download, not a client route, so a plain anchor is also
              the correct element. */}
          {audit.report_file_id && (
            <a
              className="inline-flex h-8 items-center justify-center rounded-md border border-gray-300 bg-transparent px-3 text-xs font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              href={`/api/agent/files/${audit.report_file_id}`}
              target="_blank"
              rel="noreferrer"
            >
              Open HTML report
            </a>
          )}
          <a
            className="text-sm underline text-[var(--app-muted)]"
            href={`/api/ui-audits/${audit.id}/report.md`}
            target="_blank"
            rel="noreferrer"
          >
            Markdown
          </a>
        </div>
      </Card>

      {pages.map((page) => (
        <Card key={page.url} className="p-4">
          <h3 className="font-semibold">{page.name}</h3>
          <div className="text-xs text-[var(--app-muted)] break-all">{page.url}</div>
          {page.views.map((view) => <ViewSection key={view.viewport} view={view} />)}
        </Card>
      ))}

      {!pages.length && audit.status === 'done' && (
        <Card className="p-8 text-center text-[var(--app-muted)]">
          No findings{severity ? ` at severity "${severity}"` : ''} across the audited views.
        </Card>
      )}
    </PageShell>
  );
}
